<section class=" bg-charcoal relative overflow-hidden" aria-labelledby="how-it-works-heading">
  <!-- Background decorative elements -->
  <div class="absolute inset-0 bg-grid-pattern opacity-5"></div>

  <div class="container mx-auto px-4 md:px-6 relative z-10">
    <!-- Section header -->
    <div class="text-center mb-4 lg:mb-16" data-aos="fade-up">
      <h2 id="how-it-works-heading" class="section-title">How MyPart AI Works</h2>
      <p class="text-lg text-white/70 max-w-3xl mx-auto mt-4">
        Our streamlined two-step process makes finding the perfect song for your visual content effortless.
      </p>
    </div>

    <!-- Process steps -->
    <div class="max-w-5xl mx-auto">
      <div class="flex flex-col md:flex-row gap-8 md:gap-16 items-center mb-16">
        <!-- Step 1 -->
        <div class="flex-1 order-2 md:order-1" data-aos="fade-right">
          <div class="flex items-center mb-4">
            <div
                class="w-10 h-10 rounded-full bg-gradient-to-r from-electric-blue to-blue-600 flex items-center justify-center text-white font-bold">
              1
            </div>
            <h3 class="text-2xl md:text-3xl font-bold text-white ml-4">Upload Visual Content</h3>
          </div>

          <p class="text-white/70 mb-6">
            Simply upload your image or video through our API or user interface. Our AI immediately begins analyzing
            hundreds of visual elements including colors, objects, scenes, emotions, and actions.
          </p>

          <ul class="space-y-3">
            <li class="flex items-start">
              <span class="text-electric-blue mr-2">→</span>
              <span class="text-white/80">Support for all major image and video formats</span>
            </li>
            <li class="flex items-start">
              <span class="text-electric-blue mr-2">→</span>
              <span class="text-white/80">Optional text prompts to refine the search</span>
            </li>
            <li class="flex items-start">
              <span class="text-electric-blue mr-2">→</span>
              <span class="text-white/80">Batch processing capabilities for multiple files</span>
            </li>
          </ul>
        </div>

        <!-- Step 1 Visual -->
        <div class="flex-1 order-1 md:order-2" data-aos="fade-left">
          <div class="relative">
            <div
                class="aspect-square md:aspect-video rounded-xl overflow-hidden shadow-glow border border-white/10 bg-dark-blue flex items-center justify-center p-8">
              <div class="relative w-full max-w-xs">
                <div
                    class="absolute inset-0 bg-gradient-to-r from-electric-blue/20 to-magenta/20 opacity-50 rounded-lg blur-xl"></div>
                <div
                    class="relative bg-gradient-to-br from-dark-gray to-charcoal p-4 rounded-lg border border-white/10">
                  <div class="flex items-center justify-center mb-4">
                    <svg class="h-14 w-14 text-electric-blue" fill="none" stroke="currentColor"
                         viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          stroke-linecap="round" stroke-linejoin="round"
                          stroke-width="1"/>
                    </svg>
                  </div>
                  <div class="h-1 w-full bg-dark-gray rounded overflow-hidden">
                    <div class="h-full bg-gradient-to-r from-electric-blue to-magenta w-3/4 animate-pulse"></div>
                  </div>
                  <div class="mt-2 text-sm text-center text-white/50">Analyzing visual content...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step connector -->
      <div class="flex justify-center my-12" data-aos="fade-up">
        <div class="h-16 border-l-2 border-dashed border-electric-blue/30"></div>
      </div>

      <div class="flex flex-col md:flex-row gap-8 md:gap-16 items-center">
        <!-- Step 2 Visual -->
        <div class="flex-1" data-aos="fade-right">
          <div class="relative">
            <div
                class="aspect-square md:aspect-video rounded-xl overflow-hidden shadow-glow border border-white/10 bg-dark-blue flex items-center justify-center">
              <img alt="Diagram of the two-step matching workflow: upload visual content, then receive ranked song matches." class="w-full h-full object-contain rounded-xl" src="how-it-worls.webp">
            </div>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="flex-1" data-aos="fade-left">
          <div class="flex items-center mb-4">
            <div
                class="w-10 h-10 rounded-full bg-gradient-to-r from-magenta to-purple-600 flex items-center justify-center text-white font-bold">
              2
            </div>
            <h3 class="text-2xl md:text-3xl font-bold text-white ml-4">Receive Perfect Matches</h3>
          </div>

          <p class="text-white/70 mb-6">
            Within milliseconds, our AI returns a ranked list of songs that perfectly match the emotional tone, energy,
            and context of your visual content. Preview, select, and license directly through our platform.
          </p>

          <ul class="space-y-3">
            <li class="flex items-start">
              <span class="text-magenta mr-2">→</span>
              <span class="text-white/80">Ranked results with match percentage scores</span>
            </li>
            <li class="flex items-start">
              <span class="text-magenta mr-2">→</span>
              <span class="text-white/80">Preview audio directly in the platform</span>
            </li>
            <li class="flex items-start">
              <span class="text-magenta mr-2">→</span>
              <span class="text-white/80">Immediate licensing and download options</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Call to action -->
    <div class="mt-16 text-center" data-aos="fade-up">
      <a class="btn btn-primary inline-flex items-center gap-2" href="#demo">
        Try It Yourself
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 5l7 7m0 0l-7 7m7-7H3" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
        </svg>
      </a>
    </div>
  </div>
</section>
